/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import {FC, useState, useRef, useEffect} from 'react'
import {Content} from '../../core/_models'
import {useListView} from '../../core/ListViewProvider'

type Props = {
  content: Content
  index: number
}

const ContentInfoCell: FC<Props> = ({content, index}) => {
  const {setItemIdForUpdate} = useListView()
  const [showPreview, setShowPreview] = useState(false)
  const [previewPosition, setPreviewPosition] = useState({top: 0, left: 0})
  const previewRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLDivElement>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const openEditModal = () => {
    setItemIdForUpdate(content.id)
    closePreview()
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    // Set a new timeout to show preview after a short delay
    hoverTimeoutRef.current = setTimeout(() => {
      if (imageRef.current) {
        const imageRect = imageRef.current.getBoundingClientRect()
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        const previewWidth = Math.min(windowWidth * 0.8, 600)
        const previewHeight = Math.min(windowHeight * 0.8, 400)

        let left = imageRect.left + imageRect.width / 2
        let top = imageRect.top + imageRect.height / 2

        // Adjust position to prevent clipping
        if (left + previewWidth / 2 > windowWidth) {
          left = windowWidth - previewWidth / 2 - 20
        } else if (left - previewWidth / 2 < 0) {
          left = previewWidth / 2 + 20
        }

        if (top + previewHeight / 2 > windowHeight) {
          top = windowHeight - previewHeight / 2 - 20
        } else if (top - previewHeight / 2 < 0) {
          top = previewHeight / 2 + 20
        }

        setPreviewPosition({top, left})
        setShowPreview(true)
      }
    }, 300) // 300ms delay before showing preview
  }

  const handleMouseLeave = () => {
    // Clear the timeout if mouse leaves before preview shows
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }

    // Start fade out timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPreview(false)
    }, 200) // 200ms delay before hiding preview
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    openEditModal()
  }

  const closePreview = () => {
    setShowPreview(false)
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (previewRef.current && !previewRef.current.contains(event.target as Node)) {
        closePreview()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreview()
      }
    }

    if (showPreview) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [showPreview])

  return (
    <div className='d-flex align-items-center position-relative'>
      <div className='symbol symbol-oval symbol-50px overflow-hidden me-3'>
        <div
          ref={imageRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleImageClick}
          style={{cursor: 'pointer'}}
        >
          {content.avatar ? (
            <div className='symbol-label shadow-lg'>
              <img
                src={content.avatar + '&auto=format&fit=crop&w=240&q=80'}
                alt={content.name}
                className='img-fluid shadow-'
              />
            </div>
          ) : content.video ? (
            <div
              className='symbol-label shadow-lg position-relative'
              style={{backgroundColor: '#5d78ff'}}
            >
              <img src={content.thumbnailUrl} alt={content.name} className='img-fluid shadow-' />
              <div className='play-button-container position-absolute top-50 start-50 translate-middle'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='play-icon'
                  viewBox='0 0 24 24'
                  width='20'
                  height='20'
                >
                  <defs>
                    <linearGradient id='redPlayGradient' x1='0%' y1='0%' x2='100%' y2='100%'>
                      <stop offset='0%' stopColor='#D12026' />
                      <stop offset='50%' stopColor='#B01C22' />
                      <stop offset='100%' stopColor='#D12026' />
                    </linearGradient>
                    <filter id='redShadow' x='-20%' y='-20%' width='140%' height='140%'>
                      <feDropShadow dx='0' dy='2' stdDeviation='2' floodColor='rgba(0,0,0,0.3)' />
                    </filter>
                    <radialGradient id='redHighlight' cx='30%' cy='30%' r='70%'>
                      <stop offset='0%' stopColor='#FF5A5F' stopOpacity='0.8' />
                      <stop offset='100%' stopColor='#D12026' stopOpacity='0' />
                    </radialGradient>
                  </defs>
                  <circle
                    cx='12'
                    cy='12'
                    r='11'
                    fill='url(#redPlayGradient)'
                    filter='url(#redShadow)'
                  />
                  <circle cx='12' cy='12' r='11' fill='url(#redHighlight)' />
                  <path fill='#FFFFFF' d='M9.5 16.5V7.5L17 12Z' />
                </svg>
              </div>
            </div>
          ) : (
            <div
              className={clsx(
                'symbol-label fs-3',
                `bg-light-${content.name}`,
                `text-${content.name}`
              )}
            >
              {content.id}
            </div>
          )}
        </div>
      </div>

      <div onClick={openEditModal} className='d-flex flex-column cursor-pointer'>
        <span className='text-gray-800 text-hover-primary mb-1'>{content.name}</span>
        <span className='text-muted fs-8 font-monospace'>{content.id}</span>
        <span>{content.email}</span>
      </div>

      {showPreview && (
        <div
          ref={previewRef}
          className='preview-overlay'
          onMouseEnter={() => {
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current)
            }
          }}
          onMouseLeave={handleMouseLeave}
          onClick={openEditModal}
          style={{
            position: 'fixed',
            top: `${previewPosition.top}px`,
            left: `${previewPosition.left}px`,
            transform: 'translate(-50%, -50%)',
            maxWidth: '80vw',
            maxHeight: '80vh',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 12px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
            zIndex: 1000,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            animation: 'elegantPopIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            transformStyle: 'preserve-3d',
            pointerEvents: 'auto',
          }}
        >
          {content.avatar ? (
            <img
              src={content.avatar + '&auto=format&fit=crop&w=1200&q=90'}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(80vh - 60px)',
                objectFit: 'contain',
                display: 'block',
              }}
              alt={content.name || 'Preview'}
            />
          ) : content.video ? (
            <video
              src={content.video}
              autoPlay
              loop
              muted
              playsInline
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(80vh - 60px)',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : null}
          <div className='preview-title'>{content.name || 'Untitled'}</div>
        </div>
      )}

      <style>
        {`
          .preview-overlay {
            transition: opacity 0.3s ease, transform 0.3s ease;
          }
          .cursor-pointer { cursor: pointer; }
          .preview-title {
            width: 100%;
            padding: 12px 16px;
            background: rgba(255,255,255,0.9);
            border-top: 1px solid rgba(0,0,0,0.1);
            font-size: 14px;
            color: #1a1a1a;
            text-align: center;
            font-weight: 500;
          }
          @keyframes elegantPopIn {
            0% { 
              opacity: 0; 
              transform: translate(-50%, -50%) scale(0.9);
              filter: blur(2px);
            }
            100% { 
              opacity: 1; 
              transform: translate(-50%, -50%) scale(1);
              filter: blur(0);
            }
          }
        `}
      </style>
    </div>
  )
}

export {ContentInfoCell}
